/**
 * MOCK · ASSUMED (CT-5717 / CT-5719) — the Truth plane of a record: what was
 * confirmed, from which source, and how long it stays valid.
 *
 * The seed is the prototype's (record PER-0104): two deal commitments — the
 * security review documented in the migration-timeline email, and the success
 * criterion said in the QBR — one other fact due to re-confirm (the direct line
 * from an email signature), and an empty Canon. The summary line counts the
 * same seed, so "3 facts in Truth · 1 due to re-confirm" always agrees with the
 * list.
 *
 * Reads the Knowledge QA switch (`?mockKnowledge=empty|fail|readonly|action_fail`).
 */

import { getMockKnowledgeScenario } from './knowledgeSeed.mock.js';

const SECTIONS = Object.freeze(['deal_commitments', 'other_facts', 'canon']);

/** Per record: the facts reported by the user. */
let state = new Map();

const recordState = (entityType, entityId) => {
  const key = `${entityType}/${entityId}`;
  if (!state.has(key)) state.set(key, { reported: new Set() });
  return state.get(key);
};

const fact = (fields) => ({
  status: 'confirmed',
  confirmed_by_me: false,
  can_request_change: true,
  used_in: [],
  visibility: 'everyone',
  ...fields,
});

const SEED = [
  fact({
    id: 'f11',
    section: 'deal_commitments',
    type: 'deal_commitment',
    label: 'Security review requirement',
    value: 'The security review cannot start until the migration timeline has a date',
    source_kind: 'documented',
    confirmed_source: 'Email · Migration timeline, Aug 18',
    source_channel: 'email',
    confirmed_at: '2026-08-20T15:00:00Z',
    expires_at: '2026-11-18T15:00:00Z',
    quote: 'Our security review cannot start until the migration timeline has a date on it.',
    used_in: ['next_best_action', 'drafts'],
    history: [{ at: '2026-08-20T15:00:00Z', text: 'Confirmed: The security review cannot start until the migration timeline has a date' }],
  }),
  fact({
    id: 'f12',
    section: 'deal_commitments',
    type: 'deal_commitment',
    label: 'Success criterion',
    value: 'Every open escalation has a resolution date in writing',
    source_kind: 'said',
    confirmed_source: 'QBR · Aug 22',
    source_channel: 'meeting',
    confirmed_at: '2026-08-25T15:00:00Z',
    expires_at: '2026-11-23T15:00:00Z',
    confirmed_by_me: true,
    quote: 'For us, success is every open escalation having a resolution date in writing.',
    used_in: ['next_best_action'],
    history: [{ at: '2026-08-25T15:00:00Z', text: 'Confirmed by you: Every open escalation has a resolution date in writing' }],
  }),
  fact({
    id: 'f21',
    section: 'other_facts',
    type: 'other_fact',
    label: 'Direct line',
    value: '+1 (212) 555-0155',
    source_kind: 'documented',
    confirmed_source: 'Email signature',
    source_channel: 'email',
    confirmed_at: '2026-06-24T15:00:00Z',
    expires_at: '2026-09-22T15:00:00Z',
    status: 'reconfirm_due',
    used_in: ['drafts'],
    history: [
      { at: '2026-06-24T15:00:00Z', text: 'Confirmed: +1 (212) 555-0155' },
      { at: '2026-09-22T15:00:00Z', text: 'Due to re-confirm' },
    ],
  }),
];

const QUICK_FILTERS = {
  all: () => true,
  to_reconfirm: (row) => row.status === 'reconfirm_due',
  confirmed_by_me: (row) => row.confirmed_by_me,
};

const sourceLine = (row) => [row.confirmed_source, row.confirmed_at].filter(Boolean).join(' ');

const matchesSearch = (row, search = {}) => {
  const needle = (search.query ?? '').trim().toLowerCase();
  if (!needle) return true;
  const fields = search.fields?.length ? search.fields : ['label', 'value'];
  return fields.some((field) => {
    const text = field === 'source_line' ? sourceLine(row) : row[field];
    return String(text ?? '').toLowerCase().includes(needle);
  });
};

// "Needs attention first": what is due to re-confirm first (descending) or last (ascending);
// the rest keep the seed's order (a stable sort).
const attention = (row) => (row.status === 'reconfirm_due' ? 1 : 0);
const byAttention = (direction) => (a, b) => (direction === 'asc' ? 1 : -1) * (attention(a) - attention(b));

const present = (entityType, entityId) => {
  const record = recordState(entityType, entityId);
  const readonly = getMockKnowledgeScenario() === 'readonly';
  return SEED.map(({ section, ...row }) => ({
    ...row,
    section,
    report_open: record.reported.has(row.id),
    can_request_change: readonly ? false : row.can_request_change,
  }));
};

export const listMockTruthFacts = ({ entityType, entityId, quickFilter = 'all', filters = {}, search, orderBy, orderDirection = 'desc' }) => {
  const scenario = getMockKnowledgeScenario();
  if (scenario === 'fail') return { error: { status: 500, description: 'Mock Truth failure (?mockKnowledge=fail).' } };

  const kinds = filters.source_kind ?? [];
  const statuses = filters.status ?? [];
  const rows =
    scenario === 'empty'
      ? []
      : present(entityType, entityId)
          .filter(QUICK_FILTERS[quickFilter] ?? QUICK_FILTERS.all)
          .filter((row) => !statuses.length || statuses.includes(row.status))
          .filter((row) => !kinds.length || kinds.includes(row.source_kind))
          .filter((row) => matchesSearch(row, search));
  if (orderBy === 'needs_attention') rows.sort(byAttention(orderDirection));

  const sections = SECTIONS.map((key) => ({
    key,
    facts: rows.filter((row) => row.section === key).map(({ section, ...row }) => row),
  }));
  return { body: { sections }, meta: { total_items: rows.length } };
};

export const getMockTruthFact = ({ entityType, entityId, factId }) => {
  if (getMockKnowledgeScenario() === 'fail') return { error: { status: 500, description: 'Mock Truth failure (?mockKnowledge=fail).' } };
  const row = present(entityType, entityId).find((candidate) => candidate.id === factId);
  if (!row) return { error: { status: 404, description: `No fact ${factId}` } };
  const { section, ...body } = row;
  return { body, meta: {} };
};

const mutate = (entityType, entityId, factId, apply) => {
  if (getMockKnowledgeScenario() === 'action_fail') {
    return { error: { status: 500, description: 'Mock Truth failure (?mockKnowledge=action_fail).' } };
  }
  if (!SEED.some((row) => row.id === factId)) return { error: { status: 404, description: `No fact ${factId}` } };
  apply?.(recordState(entityType, entityId));
  return { body: { fact_id: factId }, meta: {} };
};

/** Opens a change request in Governance Studio; the fact stays as it is until it is approved there. */
export const requestMockFactChange = ({ entityType, entityId, factId }) => mutate(entityType, entityId, factId);

export const reportMockTruthFact = ({ entityType, entityId, factId }) =>
  mutate(entityType, entityId, factId, (record) => {
    record.reported.add(factId);
  });

/** The counts of the Knowledge summary line, from the same seed as the list. */
export const getMockTruthSummary = () => {
  const facts = getMockKnowledgeScenario() === 'empty' ? [] : SEED;
  return {
    body: {
      facts_count: facts.length,
      due_to_reconfirm: facts.filter((row) => row.status === 'reconfirm_due').length,
      canon_signed: facts.filter((row) => row.section === 'canon').length,
    },
    meta: {},
  };
};

export const __resetMockTruthForTests = () => {
  state = new Map();
};
